// Call API client using dedicated machine-to-machine token.
import { notificationConfig } from '../config/notification';
import { getCallToken, primeCallToken, clearToken } from './machineTokenService';
import { getUserAuthToken } from '../utils/authToken';

export { primeCallToken } from './machineTokenService';

const CALL_BASE_FALLBACK = 'https://annadaneshwar-cms.azurewebsites.net';

export async function placeCall(to, message) {
  if (!to || !message) throw new Error('to and message are required');

  let hasRetriedWithFreshToken = false;

  while (true) {
    const userToken = getUserAuthToken();
    const token = userToken?.token || await getCallToken();
    const outcome = await performCallRequest(to, message, token);
    if (outcome.ok) {
      return outcome.response;
    }

    if (!hasRetriedWithFreshToken && (outcome.status === 401 || outcome.status === 403)) {
      clearToken('call');
      hasRetriedWithFreshToken = true;
      continue;
    }

    throw new Error(outcome.message);
  }
}

async function performCallRequest(to, message, token) {
  const base = notificationConfig.callApiBase || CALL_BASE_FALLBACK;

  // Try both possible backend routes (old typo `outbond`, new `outbound`).
  const routes = [
    process.env.REACT_APP_CALL_API_PATH || '/api/call/outbound',
    '/api/call/outbond',
  ];

  const successFromOpaque = () => ({
    message: 'Call requested successfully (backend response blocked by CORS).',
    data: null,
  });

  const parseResponse = async (res) => {
    const data = await res.json().catch(async () => {
      const text = await res.text().catch(() => null);
      if (text) return { message: text };
      return null;
    });

    return {
      message: data?.message || 'Call placed successfully',
      data,
    };
  };

  const attemptFetch = async (fn, recordError) => {
    try {
      return await fn();
    } catch (err) {
      recordError(err.message);
      return null;
    }
  };

  let lastErrorText = '';
  let lastStatus;

  const setLastErrorText = (msg) => {
    if (msg) {
      lastErrorText = msg;
    }
  };

  for (const path of routes) {
    const makeRequestsFor = () => {
      const url = `${base.replace(/\/$/, '')}${path}?to=${encodeURIComponent(to)}&message=${encodeURIComponent(message)}`;

      const authed = () => fetch(url, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'omit',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const withTokenQuery = (noCors = false) => {
        const fallbackUrl = `${url}&access_token=${encodeURIComponent(token)}`;
        return fetch(fallbackUrl, {
          method: 'POST',
          mode: noCors ? 'no-cors' : 'cors',
          cache: 'no-cache',
          credentials: 'omit',
        });
      };

      return { url, authed, fallback: () => withTokenQuery(false), fallbackNoCors: () => withTokenQuery(true) };
    };

    const { url, authed, fallback, fallbackNoCors } = makeRequestsFor();
    let tryNextRoute = false;
    let triedNoCors = false;

    const attempts = [authed, fallback];

    for (let i = 0; i < attempts.length; i += 1) {
      const res = await attemptFetch(attempts[i], setLastErrorText);
      if (!res) {
        continue;
      }

      if (res.type === 'opaque') {
        return { ok: true, response: successFromOpaque() };
      }

      if (res.ok) {
        const parsed = await parseResponse(res);
        return { ok: true, response: parsed };
      }

      lastStatus = res.status;
      lastErrorText = await res.text().catch(() => '') ?? '';

      if (res.status === 404) {
        tryNextRoute = true;
        break;
      }

      if (res.status === 403 && i === attempts.length - 1) {
        const opaque = await attemptFetch(fallbackNoCors, setLastErrorText);
        if (opaque?.type === 'opaque') {
          return { ok: true, response: successFromOpaque() };
        }
      }

      // Continue loop to try next attempt (fallback)
    }

    // If both authed and query attempts failed due to CORS/preflight, try a final no-cors request with token in query.
    if (!tryNextRoute && !triedNoCors && !lastStatus) {
      triedNoCors = true;
      const opaque = await attemptFetch(fallbackNoCors, setLastErrorText);
      if (opaque?.type === 'opaque') {
        return { ok: true, response: successFromOpaque() };
      }
    }

    if (tryNextRoute) {
      console.warn(`Call API route ${url} returned 404, trying next route...`);
      continue;
    }

    // Only break out if we hit a non-404 error so we surface it below.
    if (lastStatus && lastStatus !== 404) {
      break;
    }
  }

  const hint = !lastErrorText ? 'Possible CORS/preflight rejection, wrong path, or empty error body.' : lastErrorText;
  const failureStatus = typeof lastStatus === 'number' ? lastStatus : null;

  if (failureStatus === 403) {
    return {
      ok: false,
      status: failureStatus,
      message: `CORS or auth blocked (403). Check API CORS and Auth0 audience/client. ${hint}`,
    };
  }

  return {
    ok: false,
    status: failureStatus,
    message: `Place call failed (${failureStatus ?? 'network error'}): ${hint}`,
  };
}

const callApi = {
  placeCall,
  primeCallToken,
};

export default callApi;
